import { useState, useEffect, useRef, useMemo } from "react";
import { Header, BottomNav } from "../components/Header";
import { ProductCard } from "../components/ProductCard";
import { Gavel, ShoppingBag, Clock, CheckCircle, ChevronRight, Star, MessageSquare } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import BannerCarousel from "../components/BannerCarousel";
import { ProductOverlayGrid, DesktopHomeLayout } from "../components/ProductOverlayGrid";
import { BannerFeed } from "../components/BannerFeed";
import Footer from "../components/Footer";
import { WhatsAppBadge } from "../components/WhatsAppBadge";
import { toast } from "sonner";

// Section Header Component - Compact for mobile
const SectionHeader = ({ icon: Icon, title, subtitle, color, bgColor, linkTo, linkText }) => (
  <div className="flex items-center justify-between mb-2 px-4">
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 ${bgColor || 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
        <Icon size={16} className={color || 'text-gray-600'} />
      </div>
      <div>
        <h2 className="font-bold text-[#0F172A] text-sm">{title}</h2>
        <p className="text-[10px] text-[#64748B]">{subtitle}</p>
      </div>
    </div>
    {linkTo && (
      <Link to={linkTo} className="text-xs font-medium flex items-center gap-0.5 text-[#64748B] hover:text-[#0F172A]">
        {linkText || "Ver todo"}
        <ChevronRight size={14} />
      </Link>
    )}
  </div>
);

// Horizontal Scroll Product List
const HorizontalProductList = ({ products, auctionStatus, emptyMessage, hideBadge = false, noGrayscale = false }) => {
  if (!products || products.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-[#94A3B8]">
        <p className="text-sm">{emptyMessage || "No hay productos"}</p>
      </div>
    );
  }

  return (
    <div className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-3" style={{WebkitOverflowScrolling: 'touch'}}>
      {products.map((product) => (
        <div key={product.product_id} className="flex-shrink-0 w-40">
          <ProductCard 
            product={product}
            compact={true}
            auctionStatus={auctionStatus}
            horizontal={true}
            hideBadge={hideBadge}
            noGrayscale={noGrayscale}
          />
        </div>
      ))}
    </div>
  );
};

const HomePage = () => {
  const { user, token } = useAuth();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auctionStatus, setAuctionStatus] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [mobileLayout, setMobileLayout] = useState("classic");

  useEffect(() => {
    fetchAllProducts();
    fetchReviews();
    axios.get(`${API}/settings/mobile-layout`).then((r) => setMobileLayout(r.data.layout)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && token) {
      fetchAuctionStatus();
      const interval = setInterval(fetchAuctionStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/public?limit=10&t=${Date.now()}`);
      setReviews(response.data || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta reseña?")) return;
    
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      toast.success("Reseña eliminada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar reseña");
    }
  };

  const fetchAllProducts = async () => {
    setLoading(true);
    try {
      // Fetch all products including scheduled and sold
      const [activeRes, scheduledRes, soldRes] = await Promise.all([
        axios.get(`${API}/products?t=${Date.now()}`),
        axios.get(`${API}/products/scheduled?t=${Date.now()}`).catch(() => ({ data: [] })),
        axios.get(`${API}/products/sold?t=${Date.now()}`).catch(() => ({ data: [] }))
      ]);
      
      // Combine all products with a type marker
      const active = (activeRes.data || []).map(p => ({ ...p, _section: p.sale_type }));
      const scheduled = (scheduledRes.data || []).map(p => ({ ...p, _section: 'scheduled' }));
      const sold = (soldRes.data || []).map(p => ({ ...p, _section: 'sold' }));
      
      setAllProducts([...active, ...scheduled, ...sold]);
    } catch (error) {
      console.error("Error fetching products:", error);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuctionStatus = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/my/auction-status`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAuctionStatus(response.data);
    } catch (error) {
      // Silently fail
    }
  };

  // Separate products by category
  const { auctionProducts, directProducts, scheduledProducts, soldProducts } = useMemo(() => {
    const now = new Date();
    
    const auctions = allProducts.filter(p => 
      p.sale_type === 'auction' && 
      p._section !== 'sold' && 
      p._section !== 'scheduled' &&
      !p.is_sold
    );
    
    const direct = allProducts.filter(p => 
      p.sale_type === 'direct' && 
      p._section !== 'sold' && 
      p._section !== 'scheduled' &&
      !p.is_sold
    );
    
    const scheduled = allProducts.filter(p => p._section === 'scheduled');
    const sold = allProducts.filter(p => p._section === 'sold' || p.is_sold);
    
    return {
      auctionProducts: auctions,
      directProducts: direct,
      scheduledProducts: scheduled,
      soldProducts: sold.slice(0, 10) // Limit sold products
    };
  }, [allProducts]);

  // Fallback: if there are no active auctions, show "Mejor Oferta" products so this section isn't blank
  const auctionsFallbackActive = auctionProducts.length === 0 && directProducts.length > 0;
  const auctionSectionProducts = auctionsFallbackActive ? directProducts : auctionProducts;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      <main className="pb-20 pt-2">
        <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Banner Carousel */}
            <BannerCarousel />
            
            {/* Desktop: 4-Grid Layout */}
            <DesktopHomeLayout 
              auctionProducts={[...auctionProducts, ...scheduledProducts.filter(p => p.sale_type === 'auction')]}
              directProducts={[...directProducts, ...scheduledProducts.filter(p => p.sale_type === 'direct')]}
              finishedProducts={soldProducts}
              reviews={reviews}
              onDeleteReview={handleDeleteReview}
              isAdmin={user?.is_admin}
              token={token}
              API={API}
            />
            
            {/* Mobile: NEW Banner Feed layout (switchable from admin menu) */}
            {mobileLayout === "banner" && (
              <BannerFeed
                auctionProducts={auctionProducts}
                directProducts={directProducts}
                scheduledProducts={scheduledProducts}
                soldProducts={soldProducts}
                reviews={reviews}
              />
            )}

            {/* Mobile: CLASSIC layout - Section: Subastas en Vivo - Overlay Grid Style */}
            {mobileLayout !== "banner" && (
            <>
            <section className="pt-1 pb-0.5 md:hidden" data-testid="section-auctions">
              <SectionHeader
                icon={Gavel}
                title="Subastas en Vivo"
                subtitle={auctionsFallbackActive ? "Sin subastas · mira Mejor Oferta" : "Participa y gana"}
                color="text-blue-600"
                bgColor="bg-blue-100"
                linkTo="/section/auctions"
                linkText="Ver todo"
              />
              <ProductOverlayGrid
                products={auctionSectionProducts}
                isAuction={true}
                emptyMessage="No hay subastas activas"
              />
            </section>

            {/* Mobile: Section: Mejor Oferta Aceptada - Overlay Grid Style */}
            <section className="pt-1 pb-0.5 md:hidden" data-testid="section-direct">
              <SectionHeader
                icon={ShoppingBag}
                title="Mejor Oferta Aceptada"
                subtitle="Envía tu mejor oferta"
                color="text-emerald-600"
                bgColor="bg-emerald-100"
                linkTo="/section/direct"
                linkText="Ver todo"
              />
              <ProductOverlayGrid
                products={directProducts}
                isAuction={false}
                emptyMessage="No hay productos en venta directa"
              />
            </section>

            {/* Section: Finalizados */}
            {soldProducts.length > 0 && (
              <section className="py-1 bg-[#F1F5F9] md:hidden" data-testid="section-sold">
                <SectionHeader
                  icon={CheckCircle}
                  title="Finalizados"
                  subtitle="Historial de ventas"
                  color="text-gray-500"
                  bgColor="bg-gray-200"
                  linkTo="/section/sold"
                  linkText="Ver todo"
                />
                <HorizontalProductList
                  products={soldProducts}
                  auctionStatus={auctionStatus}
                  emptyMessage="No hay productos finalizados"
                  hideBadge={true}
                  noGrayscale={true}
                />
              </section>
            )}

            {/* Section: Comentarios de Compradores - Compact Mobile Only */}
            {reviews.length > 0 && (
              <section className="py-1 bg-white md:hidden" data-testid="section-reviews">
                <div className="flex items-center justify-between mb-2 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <MessageSquare size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-[#0F172A] text-sm">Comentarios 🇵🇦</h2>
                      <p className="text-[10px] text-[#64748B]">Lo que dicen nuestros compradores</p>
                    </div>
                  </div>
                </div>
                
                <div className="px-4 space-y-2">
                  {reviews.slice(0, 3).map((review) => (
                    <div 
                      key={review.review_id} 
                      className="bg-[#FAFAF9] rounded-lg p-2.5 border border-[#E2E8F0]"
                      data-testid={`review-${review.review_id}`}
                    >
                      <div className="flex gap-2">
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarImage src={review.user_picture || review.reviewer_picture} />
                          <AvatarFallback className="bg-[#475569] text-white text-xs">
                            {(review.user_name || review.reviewer_name)?.charAt(0)?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="flex items-center gap-1 min-w-0">
                              <span className="font-semibold text-[#0F172A] text-xs truncate">{review.reviewer_name || review.user_name}</span>
                              {review.reviewer_auth_method === "whatsapp" && <WhatsAppBadge size="xs" />}
                            </span>
                            <div className="flex items-center gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  size={10} 
                                  className={i < review.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"} 
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-[#374151] line-clamp-2">{review.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Section: Próximamente - At the end - Mobile Only */}
            {scheduledProducts.length > 0 && (
              <section className="py-1 md:hidden" data-testid="section-scheduled">
                <SectionHeader
                  icon={Clock}
                  title="Próximamente"
                  subtitle="Activa recordatorios"
                  color="text-amber-600"
                  bgColor="bg-amber-100"
                  linkTo="/section/scheduled"
                  linkText="Ver todo"
                />
                <HorizontalProductList
                  products={scheduledProducts}
                  auctionStatus={auctionStatus}
                  emptyMessage="No hay productos programados"
                  hideBadge={true}
                />
              </section>
            )}
            </>
            )}

            {/* Empty state if no products at all */}
            {allProducts.length === 0 && !loading && (
              <div className="text-center py-16 px-4">
                <ShoppingBag size={64} className="mx-auto text-[#CBD5E1] mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A]">No hay productos disponibles</h3>
                <p className="text-sm text-[#64748B] mt-2">
                  Los productos aparecerán aquí cuando estén disponibles
                </p>
              </div>
            )}
          </>
        )}
        </div>
        
        {/* Footer */}
        <Footer />
      </main>

      <BottomNav />
    </div>
  );
};

export default HomePage;
